"use client";

import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useCourseAnalytics } from "../analytics/useCourseAnalytics";
import { courseAnalyticsContext } from "../analytics/courseAnalytics";
import { useRef } from "react";
import { useRouter } from "next/navigation";
import { courseDetailPath } from "../services/courseDetailClient";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations, useFormatter } from "next-intl";
import { Button, CourseCard, Skeleton } from "@artha/ui";
import {
  COURSE_CARD_CONTAINER,
  COURSE_LEVELS,
  LEARNING,
} from "../learning.constants";
import type { JobCoursesProps } from "../learning.types";
import { useJobCourses } from "../hooks/useJobCourses";

/** Resets in-flight UI state when navigation changes the job or creator. */
export function JobCourses(props: JobCoursesProps) {
  if (!(props.enabled ?? LEARNING.enabled)) return null;
  return (
    <CourseSection key={`${props.creatorHandle}:${props.jobId}`} {...props} />
  );
}

/** Uses the same card border and typography as public jobs, with landscape artwork. */
function CourseSection(props: JobCoursesProps) {
  const t = useTranslations("learning");
  const format = useFormatter();
  const state = useJobCourses(props);
  const track = useCourseAnalytics(
    { source_job_id: props.jobId },
    props.creatorHandle,
  );
  const router = useRouter();
  const rail = useRef<HTMLDivElement>(null);
  const data = state.courses.data?.pages[0];
  const items = state.courses.data?.pages.flatMap((page) => page.items);
  const fallback = data?.is_fallback ?? false;
  const loading =
    state.access.isPending ||
    (state.courses.isEnabled && state.courses.isPending);
  const unavailable =
    state.access.isError || (state.courses.isError && !items?.length);
  return (
    <section
      aria-label={t(fallback ? "fallbackTitle" : "title")}
      aria-busy={loading}
      className="my-6 min-w-0 max-w-full"
    >
      <h2 className="text-lg font-semibold text-foreground">
        {t(fallback ? "fallbackTitle" : "title")}
      </h2>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">
        {t(fallback ? "fallbackSubtitle" : "subtitle")}
      </p>
      {loading && (
        <div role="status" aria-label={t("loading")}>
          <span className="sr-only">{t("loading")}</span>
          <div
            aria-hidden="true"
            className="flex gap-3 overflow-hidden px-0.5 pb-3 pt-0.5 motion-reduce:[&_*]:animate-none"
          >
            {Array.from({ length: LEARNING.skeletonCardCount }, (_, index) => (
              <div key={index} className={COURSE_CARD_CONTAINER}>
                <div className="rounded-lg border border-black bg-background p-2">
                  <Skeleton className="aspect-video w-full rounded" />
                  <div className="space-y-3 px-1 pb-1 pt-3">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                      <Skeleton className="h-3 w-1/3" />
                      <Skeleton className="ml-auto h-3 w-1/4" />
                    </div>
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <div className="flex -space-x-2">
                      <Skeleton className="h-9 w-9 rounded-full" />
                      <Skeleton className="h-9 w-9 rounded-full" />
                      <Skeleton className="h-9 w-9 rounded-full" />
                    </div>
                    <div className="flex justify-between border-t border-border pt-3">
                      <Skeleton className="h-4 w-1/4" />
                      <Skeleton className="h-4 w-1/3" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {unavailable && (
        <div role="alert" className="text-sm text-muted-foreground">
          {t("unavailable")}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => {
              void state.access.refetch();
              void state.courses.refetch();
            }}
          >
            {t("retry")}
          </button>
        </div>
      )}
      {!loading && !unavailable && items?.length === 0 && (
        <p className="rounded-xl border border-border bg-background p-4 text-sm leading-6 text-muted-foreground">
          {t(
            fallback
              ? "emptyCatalog"
              : state.access.data === false
                ? "emptyPublic"
                : "empty",
          )}
        </p>
      )}
      {!!items?.length && (
        <>
          <div
            ref={rail}
            role="list"
            aria-label={t(fallback ? "fallbackListLabel" : "listLabel")}
            tabIndex={0}
            className="flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto overscroll-x-contain px-0.5 pb-3 pt-0.5"
          >
            {items.map((course, index) => (
              <div
                role="listitem"
                key={course.entity_id}
                className={COURSE_CARD_CONTAINER}
              >
                <CourseCard
                  title={course.title}
                  provider={course.provider_name}
                  providerLogoUrl={course.provider_logo_url}
                  description={course.description}
                  instructor={course.instructor}
                  instructors={course.instructors}
                  instructorsLabel={t("detail.instructors")}
                  lectureLabel={
                    course.lecture_count
                      ? t("lectures", { count: course.lecture_count })
                      : undefined
                  }
                  imageUrl={course.image_url}
                  imageAlt={t("imageAlt", { title: course.title })}
                  levelLabel={t(
                    COURSE_LEVELS.some((level) => level === course.level)
                      ? `levels.${course.level}`
                      : "levels.all",
                  )}
                  ratingLabel={
                    course.rating === null
                      ? undefined
                      : format.number(course.rating, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })
                  }
                  reviewsLabel={
                    course.rating_count
                      ? t("ratings", { count: course.rating_count })
                      : undefined
                  }
                  durationLabel={
                    course.duration_minutes
                      ? t("duration", {
                          hours: Math.floor(
                            course.duration_minutes / LEARNING.minutesPerHour,
                          ),
                          minutes:
                            course.duration_minutes % LEARNING.minutesPerHour,
                        })
                      : undefined
                  }
                  actionLabel={t("view")}
                  accessibleLabel={t("openCourse", { title: course.title })}
                  onSelect={() => {
                    track(ANALYTICS_EVENTS.AUD_COURSE_CLICK, {
                      ...courseAnalyticsContext(course, props.jobId),
                      position: index + 1,
                    });
                    router.push(
                      courseDetailPath(
                        props.creatorHandle,
                        course.entity_id,
                        props.jobId,
                      ),
                    );
                  }}
                />
              </div>
            ))}
          </div>
          <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>
              {t(fallback ? "fallbackCount" : "count", { count: items.length })}
            </span>
            {items.length > 1 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  aria-label={t("previous")}
                  className="rounded-full border border-black bg-white p-2"
                  onClick={() =>
                    rail.current?.scrollBy({
                      left: -rail.current.clientWidth,
                      behavior: "smooth",
                    })
                  }
                >
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={t("next")}
                  className="rounded-full border border-black bg-white p-2"
                  onClick={() =>
                    rail.current?.scrollBy({
                      left: rail.current.clientWidth,
                      behavior: "smooth",
                    })
                  }
                >
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          {state.courses.isFetchNextPageError && (
            <p role="alert" className="mt-3 text-sm text-muted-foreground">
              {t("moreError")}
            </p>
          )}
          {state.courses.hasNextPage && (
            <Button
              className="mt-3 w-full"
              variant="outline"
              disabled={state.courses.isFetching}
              onClick={() => void state.loadMore()}
            >
              {t(
                state.courses.isFetching
                  ? "loadingMore"
                  : state.courses.isFetchNextPageError
                    ? "retry"
                    : "showMore",
              )}
            </Button>
          )}
        </>
      )}
    </section>
  );
}
